import React, { Fragment, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import styles from './Users.module.scss';

import { routes } from 'enums';
import { LeftArrow, RightArrow, Search } from 'assets/icons';
import { useActions, useAppSelector, useDebounce } from 'hooks';
import { notificationThunk, notificationsSelect, userSelect } from 'state';
import { CustomButton } from 'components/CustomButton';
import { NotificationModal } from 'components/NotificationModal';
import clsx from 'clsx';
import { userThunk } from 'state/thunks/userThunk/userThunk';
import { usersSelect } from 'state/selectors/userSelectors';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { CustomDropdown } from 'ui-kit/CustomDropdown';
import { EmptyStub } from 'components/EmptyStub';
import { PhoneVerified } from 'components/PhoneVerified';
import { IconButton } from 'components/IconButton';
import { USER_STATUSES, statusColour } from 'utils';

const NotificationItem = ({ user, columns, columnWidth }: any) => {
  const navigate = useNavigate();

  const itemsObj: any = {
    'Users name': (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {user['patient.name']}
      </div>
    ),
    Phone: (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {user?.phone}
      </div>
    ),
    'Phone of relative': (
      <div style={{ width: columnWidth + '%' }} className={styles.customCol1}>
        {user['patient.phoneRelative']}
      </div>
    ),
    Email: (
      <div style={{ width: columnWidth + '%' }} className={styles.customCol1}>
        {user['patient.email']}
      </div>
    ),
    'Number of visits': (
      <div style={{ width: columnWidth + '%' }} className={styles.customCol1}>
        {user?.visitCount}
      </div>
    ),
    Address: (
      <div style={{ width: columnWidth + '%' }} className={styles.customCol1}>
        {user['patient.address']}
      </div>
    ),
    Status: (
      <div
        className={styles.customCol1}
        style={{
          color: statusColour(user.userStatus),
          fontWeight: 'bold',
          width: columnWidth + '%',
        }}
      >
        {user.userStatus}
        {/*
          A deleted row's name reads 'Deleted user' and its phone reads
          `deleted-<uuid>`, so without the date there is nothing on the row to
          tell an account that closed this morning from one that closed last
          year.
        */}
        {user.userStatus === 'Deleted' && user.deletedAt && (
          <div className={styles.statusDetail}>
            {new Date(user.deletedAt).toLocaleDateString(undefined, {
              dateStyle: 'medium',
            })}
          </div>
        )}
      </div>
    ),
    // Whether this number has ever been proven by SMS. Until 2026-09-04 sign-up
    // marked every account verified without checking anything, so this starts
    // out unconfirmed for the entire existing user base and fills in as people
    // next sign in.
    'Phone check': (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        <PhoneVerified verifiedAt={user.phoneVerifiedAt} />
      </div>
    ),
  };

  return (
    <div
      className={styles.tableItem}
      // A whole table row, not a button: it holds several cells of content, so
      // wrapping it in <button> would flatten all of that into one label and
      // is invalid once any cell gains its own control. `role="button"` plus a
      // tab stop and a key handler is the correct shape here, and matches the
      // pattern already used in AwarenessCardModal.
      role="button"
      tabIndex={0}
      onClick={() => {
        console.log('click');
        navigate(routes.MAIN.USER + '/' + user.id);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space scrolls the page by default, which is the wrong thing when the
        // element is acting as a button.
        event.preventDefault();
        navigate(routes.MAIN.USER + '/' + user.id);
      }}
    >
      {columns.map((column: any) =>
        column.isSelect ? (
          // itemsObj holds bare elements, so the key has to go on a wrapper.
          <Fragment key={column.name}>{itemsObj[column.name]}</Fragment>
        ) : null,
      )}
    </div>
  );
};

const defaultColumns = [
  {
    name: 'Users name',
    isSelect: true,
  },
  {
    name: 'Phone',
    isSelect: true,
  },
  {
    name: 'Phone of relative',
    isSelect: true,
  },
  {
    name: 'Email',
    isSelect: true,
  },
  {
    name: 'Number of visits',
    isSelect: true,
  },
  {
    name: 'Address',
    isSelect: true,
  },
  {
    name: 'Status',
    isSelect: true,
  },
  {
    name: 'Phone check',
    // Off by default. These tables divide 100% between the selected columns, so
    // every column added on by default makes all the others narrower for
    // everyone -- and this one answers a question that is asked occasionally,
    // not continuously. It is one click away in the column picker.
    isSelect: false,
  },
];

export const Users = () => {
  const navigate = useNavigate();
  const [isNotificationModal, setNotificationModal] = useState(false);
  const { getUsers } = useActions(userThunk);
  const users: any = useAppSelector(usersSelect);
  const [selectedCategory, setSelectedCategory] = useState('GP/Emergency doctor');
  const [bodyMessage, setBodyMessage] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statusValue, setStatusValue] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const [columns, setColumns] = useState(defaultColumns);

  /**
   * Each visible column takes an equal share of the row.
   *
   * This page held `columnWidth` in state exactly like Doctors, Finances,
   * Articles and Dashboard do — but, unlike all four, never called the setter.
   * It therefore kept its initial value of 100, so every one of the seven
   * columns rendered at `width: 100%` and the table laid itself out seven
   * times wider than the page: 1712px inside a 1100px window, with the overflow
   * clipped rather than scrollable, so the last columns could not be reached at
   * all. Derived rather than stored, because it is a pure function of which
   * columns are selected and there is nothing for a second copy to do but drift.
   */
  const selectedColumnCount = columns.filter(el => el.isSelect).length;
  // Guard the divide: deselecting the last column would otherwise produce
  // `width: Infinity%`.
  const columnWidth = selectedColumnCount ? 100 / selectedColumnCount : 100;

  const debouncedSearchValue = useDebounce(searchValue, 500);

  const handleColumnChange = (name: any) => {
    console.log(name);
    setColumns(
      columns.map(el => {
        if (el.name === name) {
          return {
            ...el,
            isSelect: !el.isSelect,
          };
        } else {
          return el;
        }
      }),
    );
  };

  /**
   * The status filter, sent the way the API reads it.
   *
   * This used to map `'active'`/`'blocked'` to `'true'`/`'false'` while the
   * dropdown's own values were `'Active'` and `'Blocked'` -- so every branch
   * missed, the function returned `undefined`, and `withQuery` dropped the
   * parameter. Picking a status did nothing at all, silently: the list came
   * back unfiltered and looked like a filter that had matched everything.
   *
   * The server compares `userStatus` against this string directly and ignores
   * `'all'`, which is exactly what the Doctors page has always sent.
   */
  const getStatusValue = () => statusValue;

  const itemPerPage = 20;

  /**
   * One read, keyed on every filter that changes it.
   *
   * There used to be three effects here and all three fired on mount, so a
   * single page view issued three requests for the same list -- and two of
   * them disagreed about the parameters:
   *
   *     ?q=&take=20&page=&status=       (this effect, and the mount effect)
   *     ?q=&take=20&page=&status=all    (the [statusValue] effect)
   *
   * That difference is not cosmetic. The API applies an extra `isVerify`
   * filter whenever `status` is non-empty (user.service.ts), so `status=all`
   * returns ONLY verified users while `status=` returns everyone. Three
   * requests, two different answers, and the list rendered whichever came back
   * last -- the same defect that made the dashboard's numbers change between
   * reloads.
   *
   * Collapsing them into one effect means there is one request, with one set
   * of parameters, and nothing to disagree with.
   */
  useEffect(() => {
    getUsers({
      q: debouncedSearchValue,
      take: itemPerPage,
      page: '',
      status: getStatusValue(),
    });
  }, [debouncedSearchValue, statusValue]);

  const handleAdd = () => {
    const data = {
      doctorCategories: selectedCategory,
      body: bodyMessage,
    };

    setSelectedCategory('GP/Emergency doctor');
    setBodyMessage('');
    setNotificationModal(false);
  };

  const handleLeftArrowClick = () => {
    console.log(users);
    if (users?.page > 1) {
      getUsers({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: users?.page - 1,
        status: getStatusValue(),
      });
    }
  };

  const handleRightArrowClick = () => {
    if (users?.page * itemPerPage < users.count) {
      getUsers({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: users?.page + 1,
        status: getStatusValue(),
      });
    }
  };


  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.flex}>
          <div className={styles.title}>Users</div>
          <div className={styles.pagination}>
            {users?.page * itemPerPage + 1 - itemPerPage}-{users?.page * itemPerPage} from{' '}
            {users?.count}
          </div>
          <IconButton
            label="Previous page"
            className={styles.arrowContainer}
            onClick={handleLeftArrowClick}
          >
            <LeftArrow />
          </IconButton>
          <IconButton
            label="Next page"
            className={styles.arrowContainer}
            onClick={handleRightArrowClick}
          >
            <RightArrow />
          </IconButton>
        </div>

        <div className={styles.filterflex}>
          <div className={styles.statusContainer}>
            <FormControl sx={{ m: 1, minWidth: 120 }}>
              <Select
                value={statusValue}
                onChange={e => {
                  setStatusValue(e.target.value);
                }}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                style={{ height: 40, padding: 0 }}
              >
                <MenuItem value={'all'}>Status: All</MenuItem>
                {USER_STATUSES.map(status => (
                  <MenuItem key={status} value={status}>
                    Status: {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <CustomDropdown columns={columns} handleColumnChange={handleColumnChange} />

          <div className={styles.inputContainerSearch}>
            <div className={styles.searchIconContainer}>
              <Search />
            </div>

            <input
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
              placeholder="Search by Username"
              className={styles.inputSearch}
            />
          </div>
        </div>
      </div>

      {isNotificationModal && (
        <NotificationModal setNotificationModal={setNotificationModal} />
      )}

      <div className={styles.table}>
        <div className={styles.tableHeader}>
          {columns.map(el =>
            el.isSelect ? (
              <div
                key={el.name}
                className={clsx([styles.customCol1, styles.bold])}
                style={{ width: columnWidth + '%' }}
              >
                {el.name}
              </div>
            ) : null,
          )}
        </div>

        <div className={styles.tableContent}>
          {users.rows && users.rows[0] ? (
            users?.rows?.map((user: any) => {
              return (
                <NotificationItem
                  key={user.id}
                  user={user}
                  columns={columns}
                  columnWidth={columnWidth}
                />
              );
            })
          ) : (
            <EmptyStub />
          )}
        </div>
      </div>
    </div>
  );
};
